import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/useAuth';
import { useLayout } from '@/theme/useLayout';
import { AppLogo } from './AppLogo';
import { ConfidentialNotice } from './ConfidentialNotice';
import { GuestMenu } from './GuestMenu';
import { NotificationsMenu } from './NotificationsMenu';
import { ProfileMenu } from './ProfileMenu';

/**
 * The application header (`Views/Shared/Components/Header/Default.cshtml`, v2.4.2).
 *
 * Every `data-action` the buttons carried is now a call into the layout context:
 * `toggle-theme`, `app-fullscreen`, `toggle-swap` for the mobile menu and
 * `toggle` for `set-nav-minified`. The icon pairs are unchanged — which of each
 * pair is visible is decided in CSS from `html[data-bs-theme]` and
 * `body.fullscreen-active`, both written by LayoutProvider, so nothing here has
 * to know the current state to draw the right glyph.
 *
 * The search form, settings button, virtual-assistant button and print action are
 * commented out in the Razor source and are therefore not ported.
 */
export function Header() {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { toggleColorMode, toggleMobileMenu, toggleLayoutClass, isFullscreen, toggleFullscreen } =
    useLayout();

  return (
    <header className="app-header">
      {/* Toggle first, logo second — the reverse of the SmartAdmin original.
          The logo box there was sized to the nav width, so collapsing the sidebar
          resized it and dragged the toggle button along with it: the control you
          just clicked moved out from under the pointer. Leading with the toggle
          fixes it at the left edge whatever the sidebar is doing, and leaves the
          logo at a constant offset behind it. */}
      <div className="d-flex flex-grow-1 w-100 me-auto align-items-center">
        {isAuthenticated && (
          <>
            <button
              type="button"
              className="mobile-menu-icon me-2 d-flex d-sm-flex d-md-flex d-lg-none flex-shrink-0"
              onClick={toggleMobileMenu}
              aria-label={t('ToggleMobileMenu', 'Toggle mobile menu')}
            >
              <svg className="sa-icon">
                <use href="/myui/icons/sprite.svg#menu" />
              </svg>
            </button>

            <button
              type="button"
              className="collapse-icon me-3 d-none d-lg-inline-flex d-xl-inline-flex d-xxl-inline-flex"
              onClick={() => toggleLayoutClass('set-nav-minified')}
              aria-label={t('ToggleNavigationSize', 'Toggle navigation size')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 5 8">
                <polygon fill="#878787" points="4.5,1 3.8,0.2 0,4 3.8,7.8 4.5,7 1.5,4" />
              </svg>
            </button>
          </>
        )}

        <AppLogo />
        <ConfidentialNotice />
      </div>

      <button
        type="button"
        className="btn btn-system"
        onClick={toggleColorMode}
        aria-label={t('ToggleDarkMode', 'Toggle dark mode')}
      >
        <svg className="sa-icon sa-icon-2x sa-mode-light">
          <use href="/myui/icons/sprite.svg#sun" />
        </svg>
        <svg className="sa-icon sa-icon-2x sa-mode-dark">
          <use href="/myui/icons/sprite.svg#moon" />
        </svg>
      </button>

      <button
        type="button"
        className="btn btn-system d-none d-sm-block d-sm-none d-md-none d-lg-block"
        onClick={toggleFullscreen}
        aria-label={t('Fullscreen', 'Fullscreen')}
        aria-pressed={isFullscreen}
      >
        <i className="bi bi-arrows-fullscreen sa-fullscreen-off" style={{ fontSize: '1.25rem' }} />
        <i className="bi bi-fullscreen-exit sa-fullscreen-on" style={{ fontSize: '1.25rem' }} />
      </button>

      {isAuthenticated && <NotificationsMenu />}

      {user ? <ProfileMenu user={user} /> : <GuestMenu />}
    </header>
  );
}
